/**
 * Modal de criação de TV — TH-53163 + PSHWO-57471.
 *
 * POST /stores/:storeId/tvs com `{ name, area_id, ...operação }` devolve
 * TVCreated (com `token`). Após sucesso, chama `onCreated(tvId)` pra que
 * a página pai abra o modal do QR.
 *
 * PSHWO-57471: campos de operação (physical_location, installed_at,
 * homologated_model, responsible, notes) expostos como seção colapsável
 * opcional no cadastro.
 */

import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Select } from "@/components/ui/Select";

const schema = z.object({
  name: z.string().min(1, "Nome obrigatório").max(120),
  area_id: z.string().optional(),
  // Campos de operação — opcionais na criação (PSHWO-57471)
  physical_location: z.string().max(255).optional(),
  installed_at: z.string().optional(),
  homologated_model: z.string().max(120).optional(),
  responsible: z.string().max(120).optional(),
  notes: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

interface AreaOption {
  id: string;
  name: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  storeId: string;
  areas: AreaOption[];
  onCreated: (tvId: string) => void;
}

interface TVCreated {
  id: string;
  name: string;
  token: string;
}

async function createTV(
  storeId: string,
  payload: {
    name: string;
    area_id?: string;
    physical_location?: string;
    installed_at?: string;
    homologated_model?: string;
    responsible?: string;
    notes?: string;
  },
): Promise<TVCreated> {
  const res = await fetch(`/api/stores/${storeId}/tvs`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as TVCreated;
}

export function NewTVDialog({
  open,
  onOpenChange,
  storeId,
  areas,
  onCreated,
}: Props) {
  const queryClient = useQueryClient();
  const [serverError, setServerError] = useState<string | null>(null);
  const [showOperation, setShowOperation] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      area_id: "",
      physical_location: "",
      installed_at: "",
      homologated_model: "",
      responsible: "",
      notes: "",
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        name: "",
        area_id: "",
        physical_location: "",
        installed_at: "",
        homologated_model: "",
        responsible: "",
        notes: "",
      });
      setServerError(null);
      setShowOperation(false);
    }
  }, [open, reset]);

  const mutation = useMutation({
    mutationFn: (data: FormData) =>
      createTV(storeId, {
        name: data.name,
        area_id: data.area_id?.trim() ? data.area_id : undefined,
        physical_location: data.physical_location?.trim() || undefined,
        installed_at: data.installed_at?.trim() || undefined,
        homologated_model: data.homologated_model?.trim() || undefined,
        responsible: data.responsible?.trim() || undefined,
        notes: data.notes?.trim() || undefined,
      }),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ["tvs", storeId] });
      onOpenChange(false);
      onCreated(created.id);
    },
    onError: (err: Error) => setServerError(err.message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>Nova TV</DialogTitle>

        <form
          onSubmit={handleSubmit((data) => mutation.mutate(data))}
          className="mt-4 flex flex-col gap-4"
          noValidate
        >
          {/* ── Identificação ── */}
          <div className="flex flex-col gap-2">
            <Label htmlFor="tv-name">Nome</Label>
            <Input
              id="tv-name"
              autoFocus
              placeholder="TV Caixa 1"
              aria-invalid={errors.name ? true : undefined}
              {...register("name")}
            />
            {errors.name ? (
              <span role="alert" className="text-xs text-[var(--color-destructive)]">
                {errors.name.message}
              </span>
            ) : null}
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="tv-area">Área</Label>
            <Select id="tv-area" {...register("area_id")}>
              <option value="">— sem área —</option>
              {areas.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </Select>
          </div>

          {/* ── Operação (colapsável, opcional) ── */}
          <div className="rounded-md border border-[var(--color-border)]">
            <button
              type="button"
              onClick={() => setShowOperation((v) => !v)}
              aria-expanded={showOperation}
              className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium text-[var(--color-foreground)] hover:bg-[var(--color-border)]/20 transition-colors rounded-md"
            >
              <span>
                Operação{" "}
                <span className="ml-1 text-xs font-normal text-[var(--color-muted-foreground)]">
                  (opcional)
                </span>
              </span>
              <span className="text-[var(--color-muted-foreground)] text-xs">
                {showOperation ? "▲ ocultar" : "▼ expandir"}
              </span>
            </button>

            {showOperation ? (
              <div className="border-t border-[var(--color-border)] px-4 pb-4 pt-3 flex flex-col gap-4">
                <p className="text-xs text-[var(--color-muted-foreground)]">
                  Você pode preencher agora ou editar depois no detalhe da TV.
                </p>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="tv-physical-location">Localização física</Label>
                  <Input
                    id="tv-physical-location"
                    placeholder="Ex.: Seção Hortifruti — Parede Leste"
                    {...register("physical_location")}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="tv-installed-at">Data de instalação</Label>
                  <Input
                    id="tv-installed-at"
                    type="datetime-local"
                    {...register("installed_at")}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="tv-homologated-model">Modelo homologado</Label>
                  <Input
                    id="tv-homologated-model"
                    placeholder="Ex.: H96 Max V12"
                    {...register("homologated_model")}
                  />
                  <span className="text-xs text-[var(--color-muted-foreground)]">
                    Conforme lista em hardware-homologated.md
                  </span>
                </div>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="tv-responsible">Responsável</Label>
                  <Input
                    id="tv-responsible"
                    placeholder="Nome do técnico ou gestor da loja"
                    {...register("responsible")}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="tv-notes">Observações</Label>
                  <textarea
                    id="tv-notes"
                    rows={3}
                    placeholder="Observações livres de operação"
                    className="flex w-full rounded-md border border-[var(--color-border-strong)] bg-[var(--color-card)] px-3 py-2 text-sm text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-background)] disabled:cursor-not-allowed disabled:opacity-50 resize-y"
                    {...register("notes")}
                  />
                </div>
              </div>
            ) : null}
          </div>

          {serverError ? (
            <div
              role="alert"
              className="rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-2 text-sm text-[var(--color-destructive)]"
            >
              {serverError}
            </div>
          ) : null}

          <div className="mt-2 flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Criando…" : "Criar TV"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
